import { detailsKeys, reorderedKeys } from "pages/products/constants"
import * as APIHelper from "services/api"
import * as endPoint from "shared/constants/endpoint"
import { genId, transformObjectKeys } from "utils/app-helper"

class InventoryBusiness {
  getData = async searchParams => {
    const options = { params: searchParams }
    const response = (await APIHelper.getRequest(`${endPoint.product.search}`, options)) || {}
    let idx = 1
    return (response?.resource || []).map(x => {
      return { ...x, id: idx++ }
    })
  }

  getInventory = async searchParams => {
    const options = { params: searchParams }
    const response = (await APIHelper.getRequest(`${endPoint.product.searchInventory}`, options)) || {}
    let idx = 1
    return (response?.resource || []).map(x => {
      return { ...x, id: idx++ }
    })
  }

  getProductDetails = async params => {
    const options = { params }
    const response = await APIHelper.getRequest(`${endPoint.product.get}`, options)
    const respondResult = response ? transformObjectKeys(response, true).resource : null
    if (respondResult) {
      if (respondResult.inventory) {
        respondResult.inventory.forEach(item => {
          item.id = genId()
        })
      }
    }
    return respondResult
  }

  getProductMovement = async params => {
    const options = { params }
    const response = await APIHelper.getRequest(`${endPoint.product.movement}`, options)
    return response?.resource
      ? transformObjectKeys(response?.resource, true).map(x => {
          return { ...x, key: genId() }
        })
      : []
  }

  headerMapping = sourceFields => {
    const itemPayload = {}
    if (sourceFields) {
      itemPayload.un_number = sourceFields.un_number
      itemPayload.dg_volume = parseFloat(sourceFields.dg_volume)
      itemPayload.active = "Y"
      itemPayload.country_of_origin = sourceFields.country_of_origin
      itemPayload.description = sourceFields.product_description
      itemPayload.harmonized_code = sourceFields.harmonized_code
      itemPayload.item = sourceFields.product
      itemPayload.item_category1 = sourceFields.item_category1
      itemPayload.item_category2 = sourceFields.item_category2
      itemPayload.item_category3 = sourceFields.item_category3
      itemPayload.item_class = "eStore"
      itemPayload.list_price = parseFloat(sourceFields.list_price)
      itemPayload.long_description = sourceFields.long_description
      itemPayload.lot_controlled = sourceFields.lot_controlled || "N"
      itemPayload.packing_class = sourceFields.packing_class
      itemPayload.serial_num_track_inbound = sourceFields.serial_tracked?.includes("Inbound") ? "Y" : "N"
      itemPayload.serial_num_track_outbound = sourceFields.serial_tracked?.includes("Outbound") ? "Y" : "N"

      for (const key in itemPayload) {
        if (key === "list_price" || key === "dg_volume") {
          if (isNaN(itemPayload[key])) {
            delete itemPayload[key]
          }
          continue
        }

        if (!itemPayload[key]) {
          delete itemPayload[key]
        }
      }
    }
    return itemPayload
  }

  modifyDimensions = (listofObjects, action) => {
    const list = []
    const dimensionList = listofObjects.filter(item => (action === "DELETE" ? item.action === "DELETE" : item.action !== "DELETE"))
    if (dimensionList.length > 0) {
      dimensionList.forEach(element => {
        const itemChange = {
          action: element.iuom_id === "" ? "NEW" : action,
          conversion_qty: parseInt(element.conversion_qty),
          dimension_um: element.dimension_unit_of_measure.toString(),
          protection_value: parseInt(element.protection_value || 0),
          height: parseFloat(element.height),
          length: parseFloat(element.length),
          movement_cls: element.movement_cls ? element.movement_cls.toString() : "",
          quantity_um: element.quantity_unit_of_measure.toString(),
          sequence: parseFloat(element.sequence),
          treat_as_loose: element.treat_as_loose.toString(),
          weight: parseFloat(element.weight),
          weight_um: element.weight_unit_of_measure.toString(),
          width: parseFloat(element.width),
        }
        list.push(itemChange)
      })
    }
    return list
  }

  crossRefsNewDelete = listofObjects => {
    const list = []
    const crossRef = listofObjects.filter(item => item.action && item.action !== "CHANGE" && item.cross_reference !== "")
    if (crossRef.length > 0) {
      crossRef.forEach(element => {
        const item = {
          action: element.action.toString(),
          x_ref_item: element.cross_reference.toString(),
          quantity_um: element.quantity_unit_of_measure.toString(),
        }
        list.push(item)
      })
    }
    return list
  }

  crossRefsChange = (crossRefs, oldCrossRefs) => {
    const list = []
    const oldCrossRef = oldCrossRefs.filter(item => !crossRefs.some(x => x.cross_reference == item.cross_reference && x.quantity_unit_of_measure == item.quantity_unit_of_measure))
    const newCrossRef = crossRefs.filter(item => item.action === "CHANGE" && item.cross_reference !== "")
    if (newCrossRef.length > 0 && oldCrossRef.length > 0) {
      oldCrossRef.forEach(element => {
        const item = {
          action: "DELETE",
          x_ref_item: element.cross_reference.toString(),
          quantity_um: element.quantity_unit_of_measure.toString(),
        }
        list.push(item)
      })

      newCrossRef.forEach(element => {
        const item = {
          action: "NEW",
          x_ref_item: element.cross_reference.toString(),
          quantity_um: element.quantity_unit_of_measure.toString(),
        }
        list.push(item)
      })
    }
    return list
  }

  addData = async data => {
    const addPayload = this.headerMapping(data)
    addPayload.uoms = this.modifyDimensions(data.dimensions, "CHANGE")

    const crossReferences = this.crossRefsNewDelete(data.dimensions[0].cross_reference)
    if (crossReferences && crossReferences.length > 0) {
      addPayload.cross_references = crossReferences
    }

    const payload = { items: [addPayload] }

    const response = await APIHelper.postRequest(`${endPoint.product.post}`, payload)
    return response
  }

  updateData = async data => {
    let deletedDimensions = []
    const dimensionsDelete = data.dimensions.filter(x => x.action == "DELETE")

    // =====================================================================
    const itemPayload = this.headerMapping(data)
    // =====================================================================
    if (dimensionsDelete && dimensionsDelete.length > 0) {
      deletedDimensions = this.modifyDimensions(dimensionsDelete, "DELETE")
    }
    const modifiedDimensions = this.modifyDimensions(data.dimensions, "CHANGE")

    itemPayload.uoms = [...deletedDimensions, ...modifiedDimensions]
    // =====================================================================
    const crossRefs = data.dimensions[0].cross_reference || []
    const oldCrossRefs = data.dimensionsOld[0].cross_reference || []
    const crossRefsNewDelete = this.crossRefsNewDelete(crossRefs)
    const crossRefsChange = this.crossRefsChange(crossRefs, oldCrossRefs)

    itemPayload.cross_references = [...crossRefsChange, ...crossRefsNewDelete]
    // =====================================================================

    const payload = { items: [itemPayload] }

    const response = await APIHelper.patchRequest(`${endPoint.product.put}`, payload)
    return response
  }

  getUploadModel = data => {
    const reorderedData = {}
    const reorderedDetail = {}
    reorderedKeys.forEach(item => {
      if (item == "dg_volume" || item == "list_price") reorderedData[item] = !data[item] ? 0 : parseFloat(data[item])
      else if (data[item]) reorderedData[item] = data[item] || ""
    })

    const numProps = ["protection_value", "height", "length", "weight", "width"]
    detailsKeys.forEach(item => {
      if (numProps.includes(item)) {
        reorderedDetail[item] = !data[item] ? 0 : parseFloat(data[item])
      } else {
        reorderedDetail[item] = data[item]
      }
    })
    reorderedDetail.action = "NEW"

    reorderedData.active = "Y"
    reorderedData.item_class = "eStore"
    reorderedData.uoms = []
    reorderedData.uoms.push(reorderedDetail)
    reorderedData.uoms[0].quantity_um = "EA"
    reorderedData.uoms[0].conversion_qty = 1
    reorderedData.uoms[0].dimension_um = "M"
    reorderedData.uoms[0].sequence = 1
    reorderedData.uoms[0].weight_um = "KG"
    if (!reorderedData.uoms[0].treat_as_loose) reorderedData.uoms[0].treat_as_loose = "Y"

    if (data.x_ref_item1 || data.x_ref_item2) {
      reorderedData.cross_references = []

      const item1 = {
        action: "NEW",
        x_ref_item: data.x_ref_item1,
        quantity_um: "EA",
      }

      const item2 = {
        action: "NEW",
        x_ref_item: data.x_ref_item2,
        quantity_um: "EA",
      }

      if (data.x_ref_item1) {
        reorderedData.cross_references.push(item1)
      }
      if (data.x_ref_item2) {
        reorderedData.cross_references.push(item2)
      }
    }
    if (!data.serial_num_track_inbound) {
      reorderedData.serial_num_track_inbound = "N"
    }
    if (!data.serial_num_track_outbound) {
      reorderedData.serial_num_track_outbound = "N"
    }

    return reorderedData
  }

  uploadData = async data => {
    const modelData = data.map(x => this.getUploadModel(x))
    const payload = {
      items: modelData,
    }

    return await APIHelper.postRequest(`${endPoint.product.post}`, payload)
  }
}

export const inventoryBusiness = new InventoryBusiness()
